import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import store from '@/store';
import SvgIcon from '@/components/SvgIcon/SvgIcon.vue';
import CDropdown from '@/components/shared/CDropdown/CDropdown.vue';
import ModalImport from '@/components/Layers/modal/import/ModalImport.vue';

import { canGroupSelectedItems, canUnGroupSelectedItems } from '@/utils/layers/groupUtils';
import templateSvg from '@/assets/images/template.svg';
import canvaSvg from '@/assets/images/canva-logo.svg';
import psdSvg from '@/assets/images/photoshop.svg';
import amazonSvg from '@/assets/images/amazon-icon.svg';
import { useFileLayers } from '@/composables/externalFileParser';
import { CDropdownItem } from '@/components/shared/CDropdown';
import { ImportLayersEventData } from '@/typings/layers/layers';
import { Tabs } from '../modal/types';
import { useCanva } from '@/composables/canva';
import CButton from '@/components/shared/CButton/CButton.vue';
import LayersHeaderListPreview from '@/components/Layers/header/list-preview/LayersHeaderListPreview.vue';
import { ListElementType } from '@/typings/customilyElements/listElementType';


export default /*#__PURE__*/_defineComponent({
  __name: 'LayersHeader',
  props: {
    startWithCanva: { type: Boolean },
    title: null
  },
  emits: ['import-layers', 'user-updated', 'open-find-layers'],
  setup(__props: any, { emit }) {

const props = __props as {
    startWithCanva?: boolean;
    title?: string;
};

const { data: psdLayers, parseAndExtractLayers } = useFileLayers('PSD');
const { linkAccountToCanva } = useCanva(
    () => emit('user-updated'),
    () => preSelectTab(Tabs.Canva)
);

const showLoader = ref(false);
const isModalImportOpen = ref(false);
const preSelectedTab = ref<Tabs | undefined>(undefined);
const importFromOptions: CDropdownItem[] = [
    {
        label: 'Layers from Design/Template',
        thumb: templateSvg,
        callback: () => preSelectTab(Tabs.Templates)
    },
    {
        label: 'PSD File',
        thumb: psdSvg,
        divider: true,
        callback: () => fireInputPsdFileClick()
    },
    {
        label: 'From Canva',
        thumb: canvaSvg,
        divider: true,
        callback: () => linkAccountToCanva()
    },
    {
        label: 'Amazon File (.xlsx)',
        thumb: amazonSvg,
        divider: true,
        callback: () => fireInputAmazonFileClick()
    }
];




watch(
    () => [store.state.user, props.startWithCanva],
    async ([user, startWithCanva]) => {
        if (!user || !startWithCanva) return;
        await linkAccountToCanva();
    },
    { immediate: true }
);

const canvasElements = computed(() => {
    return store.state.storeLayers?.rawElements.canvasElements;
});

const flags = computed(() => {
    return store.state.storeLayers?.flags;
});

const isPreviewStep = computed(() => {
    return flags.value?.isPreviewStep;
});

const isReadonly = computed(() => {
    return flags.value?.readonly;
});

const areAllElementsSelected = computed(() => {
    return store.getters['storeLayers/areAllElementsSelected'];
});

const areAllElementsUnSelected = computed(() => {
    return store.getters['storeLayers/areAllElementsUnSelected'];
});

const getActiveLayers = computed(() => {
    return store.getters['storeLayers/getAllSelectedCanvasElements'];
});

// Get active canvas elements (selected elements + children of selected groups/layouts/files)
// used only to gate the bulk visibility/lock buttons.
const bulkToggleElements = computed(() => {
    return store.getters['storeLayers/getActiveCanvasElements'];
});

// Suffix appended to the bulk action names reported to Datadog: when at least one
// container (file, layout or group) is selected we report the usual action with a
// "- Group" suffix; a selection of only loose elements keeps the plain action name.
const bulkActionSuffix = computed(() => {
    const selectedItems = store.state.storeLayers?.selectedItems;
    const hasContainer = selectedItems?.some((item) =>
        [ListElementType.Group, ListElementType.PageEps].includes(item.type)
    );
    return hasContainer ? ' - Group' : '';
});

const areAllSelectedElementsUnlocked = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsUnlocked'];
});

const areAllSelectedElementsLocked = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsLocked'];
});

const areAllSelectedElementsHidden = computed(() => {
    return store.getters['storeLayers/areAllSelectedElementsHidden'];
});

const showGroupButton = computed(() => {
    if (!store.state.storeLayers?.canvas) {
        return false;
    }
    return canGroupSelectedItems(store.state.storeLayers?.canvas);
});

const showUnGroupButton = computed(() => {
    if (!store.state.storeLayers?.canvas) {
        return false;
    }
    return canUnGroupSelectedItems(store.state.storeLayers?.canvas);
});

const isLayout = computed(() => {
    const selectedItems = store.state.storeLayers?.selectedItems;
    if (!selectedItems?.length) return false;
    const groups = selectedItems.filter((item) => item.type === ListElementType.Group);
    if (groups.length === 0) return false;
    return groups.every((item) => item.element?.isLayout);
});

const importLayers = (data: ImportLayersEventData) => {
    const { handleClose, handleError, preserveId, preserveGroups, importedFileName } = data;
    emit('import-layers', {
        preserveId,
        preserveGroups,
        importedFileName: importedFileName,
        asPng: data.asPng,
        from: data.from,
        canvasElements: data.canvasElements,
        canvasGroups: data.canvasGroups,
        fileElements: data.fileElements,
        fileDimensions: data.fileDimensions,
        pageBackgroundColor: data.pageBackgroundColor,
        errorMessage: data.errorMessage,
        notFoundFonts: data.notFoundFonts,
        handleClose,
        handleError
    });
};

const selectAllUnlockedElements = (): void => {
    store.dispatch('storeLayers/selectAllUnlockedElements');
};

const selectAllElements = (): void => {
    store.dispatch('storeLayers/selectAllElements');
};

const clearSelection = (): void => {
    store.dispatch('storeLayers/clearSelectedItems');
    store.state.storeLayers?.canvas?.discardActiveObject();
};

const toggleAllVisible = (): void => {
    store.dispatch('storeLayers/toggleAllVisible');
};

const toggleAllLock = (): void => {
    store.dispatch('storeLayers/toggleAllLock');
};

const groupSelectedItems = (props: { isLayout: boolean }): void => {
    try {
        const { canvas } = store.state.storeLayers || {};
        canvas?.groupSelectedItems(props);
    } catch (error) {
        console.error('Error grouping selected elements:', error);
    }
};

const unGroupSelectedItems = (): void => {
    try {
        const { canvas } = store.state.storeLayers || {};
        canvas?.unGroupSelectedItems();
    } catch (error) {
        console.error('Error ungrouping selected elements:', error);
    }
};

const openModalImport = (): void => {
    isModalImportOpen.value = true;
};

const closeModalImport = (): void => {
    isModalImportOpen.value = false;
    preSelectedTab.value = undefined;
};

const preSelectTab = (tab: Tabs) => {
    openModalImport();
    preSelectedTab.value = tab;
};

const fireInputPsdFileClick = () => {
    const input = document.getElementById('importFromFile') as HTMLInputElement;
    if (!input) return;
    input.click();
};

const uploadPsdFile = async (event: Event) => {
    try {
        showLoader.value = true;
        const { files } = event.target as HTMLInputElement;
        if (!(files && files[0])) return;
        const file = files[0];
        if (file.type !== 'image/vnd.adobe.photoshop' && !file.name.endsWith('.psd')) {
            console.error('Invalid file type. Please select a PSD file.');
            return;
        }
        const { name } = file;
        const buffer = await file.arrayBuffer();
        await parseAndExtractLayers(buffer);
        store.dispatch('storeLayersModal/setParsedFileLayers', {
            ...psdLayers.value,
            artworkName: name,
            selectedPage: 1
        });
        preSelectTab(Tabs.ExternalLayers);
    } catch (error) {
        console.log('error', error);
    } finally {
        showLoader.value = false;
    }
};

const fireInputAmazonFileClick = () => {
    const input = document.getElementById('importAmazonFromFile') as HTMLInputElement;
    if (!input) return;
    input.click();
};

const uploadAmazonFile = async (event: Event) => {
    try {
        showLoader.value = true;
        const { files } = event.target as HTMLInputElement;
        if (!(files && files[0])) return;
        const file = files[0];

        if (
            file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
            !file.name.endsWith('.xlsx')
        ) {
            console.error('Invalid file type. Please select an Excel file.');
            return;
        }

        const buffer = await file.arrayBuffer();
        store.dispatch('storeLayersModal/setFileBuffer', buffer);
        preSelectTab(Tabs.AmazonFileXLSX);
    } catch (error) {
        console.error('Error parsing Excel file:', error);
        console.error('Error details:', {
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
        });
    } finally {
        showLoader.value = false;
    }
};

return { __sfc: true,psdLayers, parseAndExtractLayers, linkAccountToCanva, showLoader, isModalImportOpen, preSelectedTab, importFromOptions, props, emit, canvasElements, flags, isPreviewStep, isReadonly, areAllElementsSelected, areAllElementsUnSelected, getActiveLayers, bulkToggleElements, bulkActionSuffix, areAllSelectedElementsUnlocked, areAllSelectedElementsLocked, areAllSelectedElementsHidden, showGroupButton, showUnGroupButton, isLayout, importLayers, selectAllUnlockedElements, selectAllElements, clearSelection, toggleAllVisible, toggleAllLock, groupSelectedItems, unGroupSelectedItems, openModalImport, closeModalImport, preSelectTab, fireInputPsdFileClick, uploadPsdFile, fireInputAmazonFileClick, uploadAmazonFile, SvgIcon, CDropdown, ModalImport, CButton, LayersHeaderListPreview }
}

})